import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
//Limitless imports
import Grid from "@limitless/modern/layout/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import ConversationHistoryList from "@limitless/modern/components/conversation-history-list/ConversationHistoryList";
import { useExpertSupportConversationHistoryContext, useExpertProfileContext } from "@limitless/expert";
import { LoadingSpinner } from "@limitless/portal";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import Lock from "@limitless/icons/Lock";

const ConversationHistory = ({ handleMessageClick }) => {
    const { history, unReadMessages, setHistory, unReadMessageCount, setUnReadMessages } =
        useExpertSupportConversationHistoryContext();

    const {
        formatDate,
        profile: { expertTrustedLocationType }
    } = useExpertProfileContext();
    const isSecureAccess = ["platform", "tenant"].includes(expertTrustedLocationType);

    const queryString = useLocation().search;
    const query = new URLSearchParams(useLocation().search);
    const querySearchMessageId = query.get("messageId");

    const updateHistoryMessage = currentMessageId => {
        if (unReadMessages.includes(currentMessageId)) {
            const messages = unReadMessages.filter(item => item !== currentMessageId);
            setUnReadMessages(messages);
            history.activeMessages.forEach(message => {
                if (currentMessageId === message.messageId) {
                    message.isUnreadMessage = false;
                }
            });
            setHistory(history);
        }
    };

    React.useEffect(() => {
        if (queryString && unReadMessageCount !== 0 && unReadMessages.includes(querySearchMessageId)) {
            updateHistoryMessage(querySearchMessageId);
        }
    }, [unReadMessages, unReadMessageCount, queryString]);

    const onMessageClick = message => {
        updateHistoryMessage(message.messageId);
        handleMessageClick(message);
    };

    const renderNoMessageTypography = React.useMemo(
        () => (
            <Typography variant="body2" color="text.secondary">
                {"No messages"}
            </Typography>
        ),
        [history]
    );

    const historyMessages = React.useMemo(
        () => [
            { label: "Open conversations", data: history.activeMessages },
            {
                label: "Closed conversations",
                data: history.previousMessages
            }
        ],
        [history]
    );

    if (history?.loading && !querySearchMessageId) {
        return (
            <Grid container justifyContent="center">
                <LoadingSpinner />
            </Grid>
        );
    }

    return historyMessages.map((item, index) => (
        <Grid key={index}>
            <div style={{ display: "flex", alignItems: "top", gap: "6px" }}>
                <Typography variant="h6" mb={3} mt={index === 0 ? 0 : 4}>
                    {item.label}
                </Typography>
                {index === 0 && isSecureAccess && (
                    <IconButton
                        icon={<Lock />}
                        label="Showing additional conversations based on your Secure location"
                        id="conversationHistoryLockIcon"
                        sx={{
                            width: 23,
                            height: 23
                        }}
                    />
                )}
            </div>
            {item.data.length > 0 ? (
                <ConversationHistoryList
                    items={item.data}
                    handleMessageClick={onMessageClick}
                    formatDate={formatDate}
                />
            ) : (
                renderNoMessageTypography
            )}
        </Grid>
    ));
};

export default ConversationHistory;

ConversationHistory.propTypes = {
    handleMessageClick: PropTypes.func.isRequired
};
